import React from 'react'
import {Lang} from 'yougile-lang'
import {Util} from 'yougile-util'
import {DataObjectId, Hub, UserEvent} from 'yougile-model'
import {Backend} from '@common/backend'
import {getUserProxyByUserId} from '@components/common/base/avatar/use-user-proxy'
import {noPropagate} from '@containers/hooks'
import {StatsTimelineRowUsername} from '../item/username'
import {useStatsTimelineStore} from '../stats-timeline-store'
import FileSVG from './file.svg'
import {getTitle, getUserNamesInString} from './utils'

const extractText = (text: string) => {
  return text.replace(/\/root\/#file:\/user-data\/[^ \n\t]+/g, '').trim()
}

interface GetNamesProps {
  users: DataObjectId[]
}

const GetNames = ({users}: GetNamesProps) => {
  const [state] = useStatsTimelineStore()
  if (state.view === 'table') {
    if (users.length > 2) {
      return (
        <div title={getUserNamesInString(users)}>
          {users.length} {Lang.msg.statsTimeline.statObjectUsers}
        </div>
      )
    }
    return (
      <div title={getUserNamesInString(users)}>
        {users
          .slice(0, 2)
          .map(user => getUserProxyByUserId(user))
          .filter(Boolean)
          .map(user => {
            return <StatsTimelineRowUsername key={user.userId} user={user} />
          })}
      </div>
    )
  }
  return (
    <>
      {users
        .map(user => getUserProxyByUserId(user))
        .filter(Boolean)
        .map(user => {
          return <StatsTimelineRowUsername key={user.userId} user={user} />
        })}
    </>
  )
}

interface ObjectNameProps {
  event: UserEvent
  obj: Hub
}

// eslint-disable-next-line max-lines-per-function
export const ObjectName = ({event, obj}: ObjectNameProps) => {
  const [state] = useStatsTimelineStore()
  switch (event.type) {
    case 'message': {
      const m = obj.chat.messages.get(event.time)
      const text = m ? extractText(m.text) : ''
      if (state.view === 'table') {
        return (
          <span className="flex-1 truncate" title={text}>
            “{Util.limitStr(text, 100)}”
          </span>
        )
      }
      return <span>“{Util.limitStr(text, 100)}”</span>
    }
    case 'file': {
      if (state.view === 'table' && event.urls.length > 2) {
        return (
          <div
            className="flex-1 truncate"
            title={event.urls
              .map(url => url.split('/').pop().split('?')[0])
              .join(', ')}
          >
            {event.urls.length} {Lang.msg.statsTimeline.items}
          </div>
        )
      }
      return (
        <div
          className={`flex ${
            state.view === 'card' ? 'flex-wrap' : 'flex-1 truncate'
          } `}
        >
          {event.urls.map(url => {
            return (
              <div className="flex mr-6" key={url}>
                <div className="flex text-action-default">
                  <FileSVG />
                </div>
                <a
                  href={Backend.toDataUrl(url)}
                  target="_blank"
                  onClick={noPropagate}
                >
                  <div title={url.split('/').pop().split('?')[0]}>
                    {url.split('/').pop().split('?')[0]}
                  </div>
                </a>
              </div>
            )
          })}
        </div>
      )
    }
    case 'rename': {
      const fromTo = Lang.msg.statsTimeline.fromTo(event.oldName, event.newName)
      if (state.view === 'table') {
        return (
          <span title={fromTo} className="flex-1 truncate">
            {fromTo}
          </span>
        )
      }
      return <span>{fromTo}</span>
    }
    case 'users':
    case 'add-user-to-project':
    case 'remove-user-from-project': {
      return (
        <div className={`${state.view === 'table' ? 'flex-1 truncate' : ''}`}>
          <div className="w-6" />
          <GetNames users={event.users} />
        </div>
      )
    }
    case 'change-project-roles': {
      if (state.view === 'table' && event.roleNames.length > 2) {
        return (
          <div title={event.roleNames.join(', ')} className="flex-1 truncate">
            {event.roleNames.length} {Lang.msg.statsTimeline.items}
          </div>
        )
      }
      if (state.view === 'table') {
        return (
          <div className="flex-1 truncate">
            {event.roleNames.map(name => Util.limitStr(name, 50)).join(', ')}
          </div>
        )
      }
      return <div>{event.roleNames.join(', ')}</div>
    }
    case 'change-project-permissions': {
      return (
        <div className={`${state.view === 'table' ? 'flex-1 truncate' : ''}`}>
          <div className="w-6" />
          <GetNames users={event.users} />
        </div>
      )
    }
    case 'add':
    case 'delete':
    case 'restore':
    case 'move':
    case 'complete':
    case 'uncomplete':
    case 'done':
    case 'executor':
    case 'deadline':
    case 'string':
    case 'sprint':
    case 'undone':
    case 'timer':
    case 'timetracking':
    case 'stopwatch': {
      if (state.view === 'card') {
        return <></>
      } else {
        return (
          <span className="flex-1 truncate" title={getTitle(obj)}>
            “{getTitle(obj)}”
          </span>
        )
      }
    }
    default: {
      if (state.view === 'table') {
        return (
          <span title={getTitle(obj)} className="flex-1 truncate">
            “{getTitle(obj)}”
          </span>
        )
      }
      return <span>“{getTitle(obj)}”</span>
    }
  }
}

export const getObjectName = ({event, obj}: ObjectNameProps) => {
  switch (event.type) {
    case 'file': {
      return event.urls
        .map(url => url.split('/').pop().split('?')[0])
        .join(', ')
    }
    case 'message': {
      const m = obj.chat.messages.get(event.time)
      const text = m ? extractText(m.text) : ''
      return `“${Util.limitStr(text, 100)}”`
    }
    case 'rename': {
      return Lang.msg.statsTimeline.fromTo(event.oldName, event.newName)
    }
    case 'users':
    case 'add-user-to-project':
    case 'remove-user-from-project': {
      return getUserNamesInString(event.users)
    }
    case 'change-project-roles': {
      return event.roleNames.join(', ')
    }
    case 'change-project-permissions': {
      return getUserNamesInString(event.users)
    }
    default:
      return `“${getTitle(obj)}”`
  }
}
